/**
 * Formulário de dados do visitante para confirmar agendamento - Design Premium
 * 
 * Coleta nome, email, telefone e notas do visitante antes de
 * criar o booking via RPC create_scheduling_booking.
 * 
 * Quando deal_id presente, pré-preenche dados do contato
 * e bloqueia edição de email e telefone.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 2.0.0 - Redesign premium com resumo colorido e visual refinado
 */

import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Calendar, Clock, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCreatePublicBooking, type PublicEventType, type BookingFormData, type DealSchedulingContext } from '@/hooks/usePublicScheduling';
import { toast } from 'sonner';

interface PublicSchedulingFormProps {
  eventType: PublicEventType;
  selectedSlot: string;
  onConfirmed: (bookingId: string) => void;
  onBack: () => void;
  /** ID do deal vinculado ao agendamento (via query param) */
  dealId?: string;
  /** Dados pré-preenchidos do contato do deal */
  prefillData?: DealSchedulingContext;
}

export function PublicSchedulingForm({
  eventType,
  selectedSlot,
  onConfirmed,
  onBack,
  dealId,
  prefillData,
}: PublicSchedulingFormProps) {
  const { mutateAsync: createBooking, isPending } = useCreatePublicBooking();

  const eventColor = eventType.color || '#3B82F6';

  // Estado do formulário - inicializado com prefill quando disponível
  const [form, setForm] = useState<BookingFormData>({
    guest_name: prefillData?.guest_name || '',
    guest_email: prefillData?.guest_email || '',
    guest_phone: prefillData?.guest_phone || '',
    guest_notes: '',
  });

  // Atualizar form quando prefillData carregar (caso async)
  useEffect(() => {
    if (prefillData) {
      setForm((prev) => ({
        ...prev,
        guest_name: prefillData.guest_name || prev.guest_name,
        guest_email: prefillData.guest_email || prev.guest_email,
        guest_phone: prefillData.guest_phone || prev.guest_phone,
      }));
    }
  }, [prefillData]);

  // Indica se campos foram pré-preenchidos pelo deal
  const hasPrefill = !!prefillData;

  // Formata data e hora para exibição
  const slotDate = parseISO(selectedSlot);
  const formattedDate = format(slotDate, "EEEE, dd 'de' MMMM", { locale: ptBR });
  const formattedTime = format(slotDate, 'HH:mm');

  /** Submissão do formulário */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.guest_name.trim() || !form.guest_email.trim()) {
      toast.error('Preencha nome e email');
      return;
    }

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.guest_email.trim())) {
      toast.error('Email inválido');
      return;
    }

    try {
      const bookingId = await createBooking({
        eventTypeId: eventType.id,
        startAt: selectedSlot,
        formData: {
          guest_name: form.guest_name.trim(),
          guest_email: form.guest_email.trim(),
          guest_phone: form.guest_phone?.trim() || undefined,
          guest_notes: form.guest_notes?.trim() || undefined,
        },
        timezone: eventType.timezone,
        dealId,
      });

      onConfirmed(bookingId);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao confirmar agendamento');
    }
  };

  return (
    <div className="p-6 sm:p-8">
      {/* Header premium com botão voltar */}
      <div className="flex items-center gap-3 mb-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full hover:bg-muted"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold text-foreground">
          Seus dados
        </h2>
      </div>

      {/* Resumo da seleção - card com borda colorida premium */}
      <div
        className="mb-6 p-4 rounded-xl border-l-4 bg-muted/40"
        style={{ borderLeftColor: eventColor }}
      >
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="flex items-center gap-1.5 capitalize text-foreground font-medium">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            {formattedDate}
          </span>
          <span className="flex items-center gap-1.5 text-foreground font-medium">
            <Clock className="h-4 w-4 text-muted-foreground" />
            {formattedTime} • {eventType.duration_minutes} min
          </span>
        </div>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Nome */}
        <div className="space-y-2">
          <Label htmlFor="guest_name" className="text-sm font-medium">Nome completo *</Label>
          <Input
            id="guest_name"
            placeholder="Seu nome"
            value={form.guest_name}
            onChange={(e) => setForm((p) => ({ ...p, guest_name: e.target.value }))}
            maxLength={100}
            required
            disabled={isPending}
            className="h-11 rounded-xl"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="guest_email" className="text-sm font-medium">Email *</Label>
          <Input
            id="guest_email"
            type="email"
            placeholder="seu@email.com"
            value={form.guest_email}
            onChange={(e) => setForm((p) => ({ ...p, guest_email: e.target.value }))}
            maxLength={255}
            required
            disabled={isPending}
            className="h-11 rounded-xl"
          />
        </div>

        {/* Telefone - com seletor de DDI para evitar duplicação de contatos */}
        <div className="space-y-2">
          <Label htmlFor="guest_phone" className="text-sm font-medium">Telefone</Label>
          <PhoneInput
            id="guest_phone"
            value={form.guest_phone}
            onChange={(value) => setForm((p) => ({ ...p, guest_phone: value }))}
            disabled={isPending || (hasPrefill && !!prefillData?.guest_phone)}
            placeholder="(65) 99351-4338"
            className={hasPrefill && !!prefillData?.guest_phone ? 'opacity-50 pointer-events-none' : ''}
          />
        </div>

        {/* Notas */}
        <div className="space-y-2">
          <Label htmlFor="guest_notes" className="text-sm font-medium">Observações</Label>
          <Textarea
            id="guest_notes"
            placeholder="Informações adicionais para a reunião..."
            value={form.guest_notes}
            onChange={(e) => setForm((p) => ({ ...p, guest_notes: e.target.value }))}
            maxLength={500}
            rows={3}
            disabled={isPending}
            className="rounded-xl"
          />
        </div>

        {/* Botão confirmar - com cor do evento */}
        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            disabled={isPending || !form.guest_name.trim() || !form.guest_email.trim()}
            className="gap-2 min-w-[180px] h-11 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-md"
            style={{
              backgroundColor: eventColor,
              color: '#fff',
            }}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Agendando...
              </>
            ) : (
              'Confirmar Agendamento'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
